import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, ExternalLink as ExternalLinkIcon, Euro, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

/**
 * Read-only Fahrzeugdaten-Ansicht für Händler.
 *
 * Wird per Doppelklick aus der Buy-Request-Liste im Dashboard in einem
 * neuen Tab geöffnet, damit der Händler die kompletten Fahrzeugdaten
 * sehen kann ohne den Listenkontext zu verlieren. Kontakt-Daten des
 * Privatkunden sind hier bewusst nicht enthalten — die werden erst nach
 * Angebots-Annahme freigegeben (Backend filtert das).
 *
 * Zusätzlich: Inline-Angebotsformular am Ende der Seite, damit der
 * Händler direkt vom Detail-Tab aus ein Angebot abgeben oder aktualisieren
 * kann, ohne in die Liste zurück springen zu müssen.
 */
export default function DealerBuyRequestDetailPage() {
    const { id } = useParams();
    const { user: currentUser } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const reload = () => {
        api.get(`/buy-requests/${id}`)
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.detail || "Fehler beim Laden"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        document.title = `Fahrzeugdaten · ${id}`;
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" data-testid="brd-loading">
                <Loader2 className="w-8 h-8 animate-spin text-[#FFCC00]" />
            </div>
        );
    }
    if (error || !data) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-3" data-testid="brd-error">
                <div className="text-red-700 font-bold">{error || "Anfrage nicht gefunden"}</div>
                <Link to="/dashboard?tab=buy-requests" className="text-sm text-blue-700 hover:underline">
                    Zurück zur Übersicht
                </Link>
            </div>
        );
    }

    const images = ["img_front", "img_rear", "img_left", "img_right", "img_papers", "img_odometer", "img_engine", "img_keys"]
        .map((k) => ({ key: k, url: data[k] }))
        .filter((x) => x.url);

    return (
        <div className="min-h-screen bg-white" data-testid="dealer-buy-request-detail">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
                <Link
                    to="/dashboard?tab=buy-requests"
                    className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#6B7280] hover:text-[#111827]"
                    data-testid="brd-back"
                >
                    <ArrowLeft className="w-3.5 h-3.5" /> Zurück
                </Link>

                <div className="flex items-start justify-between gap-4 mt-3 mb-6">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-[#92400E]">Privatkunden-Ankaufanfrage</div>
                        <h1 className="text-3xl sm:text-4xl font-black text-[#111827] font-display tracking-tight mt-1">
                            {data.brand} {data.model}
                        </h1>
                        <div className="text-sm text-gray-600 mt-1">
                            {data.year} · PLZ {data.postal_code}
                            {data.mileage_km ? ` · ${Number(data.mileage_km).toLocaleString("de-DE")} km` : ""}
                            {data.power_hp ? ` · ${data.power_hp} PS` : ""}
                        </div>
                    </div>
                    {data.desired_price && (
                        <div className="text-right">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Wunschpreis</div>
                            <div className="text-2xl font-black font-mono-num text-[#111827]">
                                {Math.round(data.desired_price).toLocaleString("de-DE")} €
                            </div>
                        </div>
                    )}
                </div>

                {/* Stammdaten-Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-8" data-testid="brd-specs">
                    <SpecCell label="Marke" value={data.brand} />
                    <SpecCell label="Modell" value={data.model} />
                    <SpecCell label="Baujahr" value={data.year} />
                    <SpecCell label="Kilometerstand" value={data.mileage_km ? `${Number(data.mileage_km).toLocaleString("de-DE")} km` : "—"} />
                    <SpecCell label="Leistung" value={data.power_hp ? `${data.power_hp} PS` : "—"} />
                    <SpecCell label="HU bis" value={data.tuev_until || "—"} />
                    <SpecCell label="PLZ" value={data.postal_code} />
                    <SpecCell label="Kategorie" value={data.category === "motorcycle" ? "Motorrad" : "Fahrrad"} />
                    {data.first_registration && <SpecCell label="EZ" value={data.first_registration} />}
                    {data.condition && <SpecCell label="Zustand" value={data.condition} />}
                    {data.color && <SpecCell label="Farbe" value={data.color} />}
                    {data.frame_number && <SpecCell label="Rahmen-Nr." value={data.frame_number} mono />}
                </div>

                {data.message && (
                    <div className="border-l-4 border-[#FFCC00] bg-[#FFFBEB] p-4 rounded-r-sm mb-8" data-testid="brd-message">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-[#92400E] mb-1">Mitteilung des Verkäufers</div>
                        <p className="text-sm text-[#111827] italic">„{data.message}"</p>
                    </div>
                )}

                {/* Bilder-Galerie */}
                {images.length > 0 && (
                    <div data-testid="brd-images">
                        <h2 className="text-sm font-bold uppercase tracking-wider mb-3">Fotos vom Verkäufer ({images.length})</h2>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {images.map((img) => (
                                <a
                                    key={img.key}
                                    href={img.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="block bg-gray-100 border border-gray-200 rounded-sm overflow-hidden hover:border-[#111827] transition-colors group"
                                    data-testid={`brd-image-${img.key}`}
                                >
                                    <img src={img.url} alt={img.key} className="w-full aspect-[4/3] object-cover" />
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-600 group-hover:text-[#111827] p-2 flex items-center justify-between">
                                        {imageLabel(img.key)}
                                        <ExternalLinkIcon className="w-3 h-3" />
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                <OfferForm
                    data={data}
                    currentUser={currentUser}
                    onSubmitted={reload}
                />
                {/* Phase 4.12 (M8) — Vergleichbare Inserate auf dem Markt (KI-Match-Score) */}
                <MarketComparablesSection requestId={id} currentUser={currentUser} />
            </div>
        </div>
    );
}

function MarketComparablesSection({ requestId, currentUser }) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    const canMatch = currentUser?.role === "b2b" || currentUser?.role === "admin";
    if (!canMatch) return null;

    const run = async () => {
        setLoading(true);
        try {
            const { data: resp } = await api.post(`/buy-requests/${requestId}/ai-match`);
            setData(resp);
        } catch (e) {
            toast.error(e.response?.data?.detail || "KI-Match fehlgeschlagen");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mt-10 bg-violet-50 border-2 border-violet-200 rounded-sm p-6" data-testid="market-comparables-section">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-violet-900 mb-1">
                        ✨ KI-Marktanalyse
                    </div>
                    <h2 className="font-display font-bold text-xl">Vergleichbare Inserate</h2>
                    <p className="text-xs text-gray-600 mt-1">
                        Aktuelle aktive Marktangebote — als Kalkulations-Grundlage für dein Angebot.
                    </p>
                </div>
                <Button
                    onClick={run}
                    disabled={loading}
                    className="bg-violet-700 hover:bg-violet-800 text-white rounded-sm uppercase font-bold tracking-wider text-xs"
                    data-testid="market-comparables-load"
                >
                    {loading ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Lädt…</> : "Vergleiche laden"}
                </Button>
            </div>
            {data && data.listings.length === 0 && (
                <div className="text-sm text-gray-600 italic" data-testid="market-comparables-empty">
                    Keine vergleichbaren aktiven Inserate gefunden.
                </div>
            )}
            {data && data.listings.length > 0 && (
                <div className="space-y-2" data-testid="market-comparables-list">
                    {data.listings.slice(0, 8).map((l) => (
                        <Link
                            key={l.id}
                            to={`/listing/${l.id}`}
                            target="_blank"
                            className="block bg-white border border-violet-200 rounded-sm p-3 hover:border-violet-500 transition-colors"
                            data-testid={`market-comp-${l.id}`}
                        >
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span
                                            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${
                                                l._match.score >= 75 ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                                                l._match.score >= 50 ? "bg-amber-100 text-amber-800 border-amber-200" :
                                                "bg-gray-100 text-gray-700 border-gray-200"
                                            }`}
                                            data-testid={`market-comp-score-${l.id}`}
                                        >
                                            Match {l._match.score}
                                        </span>
                                        <span className="font-bold text-sm truncate">{l.title}</span>
                                    </div>
                                    {l._match.why && (
                                        <p className="text-[11px] text-gray-600 italic mt-1 line-clamp-2">„{l._match.why}"</p>
                                    )}
                                </div>
                                <div className="font-mono-num text-sm font-bold text-[#111827] shrink-0">
                                    {l.price ? `${l.price.toLocaleString("de-DE")} €` : "—"}
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

function OfferForm({ data, currentUser, onSubmitted }) {
    const myOffer = (data.offers || []).find((o) => o.dealer_id === currentUser?.id);
    const closed = data.status !== "open";
    const canOffer = currentUser?.role === "b2b" && currentUser?.accept_buy_requests;

    const [amount, setAmount] = useState(myOffer ? String(myOffer.amount) : "");
    const [message, setMessage] = useState(myOffer?.message || "");
    const [pickupOffered, setPickupOffered] = useState(!!myOffer?.pickup_offered);
    const [submitting, setSubmitting] = useState(false);

    if (!canOffer) {
        // Render the original informational footer so non-eligible users
        // still see why the form is missing.
        return (
            <div className="mt-10 p-4 bg-gray-50 border border-gray-200 rounded-sm text-xs text-gray-600" data-testid="brd-footer-note">
                <strong>Hinweis:</strong> Kontaktdaten des Verkäufers werden erst nach Angebots-Annahme freigegeben.
                Aktivieren Sie unter <strong>Einstellungen → Marktplatzeinstellungen → "An Geboten auf B2C-Verkaufsgesuche teilnehmen"</strong>, um Angebote abgeben zu können.
            </div>
        );
    }

    const submit = async () => {
        const n = Number(amount);
        if (!Number.isFinite(n) || n <= 0) {
            toast.error("Bitte einen gültigen Angebotsbetrag eingeben");
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/buy-requests/${data.id}/offers`, {
                amount: n,
                message: message.trim() || null,
                pickup_offered: pickupOffered || null,
            });
            toast.success(myOffer ? "Angebot aktualisiert" : "Angebot abgegeben");
            onSubmitted();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Angebot konnte nicht gesendet werden");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mt-10" data-testid="brd-offer-form">
            <div className="bg-white border-2 border-[#FFCC00] rounded-sm p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-1">
                    <Euro className="w-5 h-5 text-[#FFCC00]" />
                    <h2 className="font-display font-bold text-xl">
                        {myOffer ? "Ihr Angebot aktualisieren" : "Angebot abgeben"}
                    </h2>
                </div>
                <p className="text-xs text-gray-600 mb-4">
                    Verbindliches Angebot an den Privatkunden. Sie können Ihr Angebot jederzeit anpassen, solange die Anfrage offen ist. Bei Annahme werden Ihre Kontaktdaten freigegeben.
                </p>

                {myOffer && (
                    <div className="mb-4 p-3 bg-[#FFFBEB] border border-[#FDE68A] rounded-sm text-xs text-[#92400E] flex items-center gap-2" data-testid="brd-existing-offer-banner">
                        <CheckCircle2 className="w-4 h-4 shrink-0" />
                        <span>
                            Aktuelles Angebot: <strong className="font-mono-num">{Number(myOffer.amount).toLocaleString("de-DE")} €</strong>
                            {" "}— Status: <strong>{myOffer.status}</strong>
                        </span>
                    </div>
                )}

                {closed && (
                    <div className="mb-4 p-3 bg-gray-50 border border-gray-300 rounded-sm text-xs text-gray-700" data-testid="brd-closed-banner">
                        Diese Anfrage ist nicht mehr offen — neue Angebote sind deaktiviert.
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Angebotsbetrag</Label>
                        <div className="relative mt-1">
                            <Input
                                type="number"
                                step="50"
                                min="1"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder={data.desired_price ? String(Math.round(data.desired_price * 0.9)) : "z.B. 2500"}
                                className="rounded-sm font-mono-num pr-8"
                                disabled={closed || submitting}
                                data-testid="brd-offer-amount"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold">€</span>
                        </div>
                        {data.desired_price && (
                            <p className="text-[11px] text-gray-500 mt-1">
                                Wunschpreis Verkäufer: <strong className="font-mono-num">{Math.round(data.desired_price).toLocaleString("de-DE")} €</strong>
                            </p>
                        )}
                    </div>
                    <div className="flex items-end">
                        <label className="flex items-start gap-2 cursor-pointer text-sm pb-2" data-testid="brd-offer-pickup-label">
                            <input
                                type="checkbox"
                                checked={pickupOffered}
                                onChange={(e) => setPickupOffered(e.target.checked)}
                                disabled={closed || submitting}
                                className="mt-0.5 w-4 h-4 accent-[#FFCC00]"
                                data-testid="brd-offer-pickup-checkbox"
                            />
                            <span>
                                <span className="font-bold text-[#111827]">Abholung anbieten</span>
                                <span className="block text-[11px] text-gray-500">Sie holen das Fahrzeug beim Verkäufer ab (kann den Zuschlag erhöhen).</span>
                            </span>
                        </label>
                    </div>
                </div>

                <div className="mb-4">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Persönliche Nachricht (optional)</Label>
                    <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="z.B. Wir prüfen das Fahrzeug nach Terminvereinbarung in unserer Werkstatt und übernehmen die Abmeldung."
                        rows={3}
                        maxLength={500}
                        disabled={closed || submitting}
                        className="rounded-sm mt-1"
                        data-testid="brd-offer-message"
                    />
                    <div className="text-[10px] text-gray-400 mt-1 text-right">{message.length}/500</div>
                </div>

                <Button
                    onClick={submit}
                    disabled={closed || submitting || !amount}
                    className="w-full bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider disabled:opacity-50"
                    data-testid="brd-offer-submit-btn"
                >
                    {submitting ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sende…</>
                    ) : (
                        <><Send className="w-4 h-4 mr-2" /> {myOffer ? "Angebot aktualisieren" : "Angebot abgeben"}</>
                    )}
                </Button>
                <p className="text-[10px] text-gray-500 text-center mt-3">
                    Mit dem Absenden bestätigen Sie die <Link to="/legal/buy-request-terms" target="_blank" className="text-blue-700 underline">Annahmebedingungen</Link>.
                </p>
            </div>
        </div>
    );
}

function SpecCell({ label, value, mono }) {
    return (
        <div className="border border-gray-200 rounded-sm p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">{label}</div>
            <div className={`text-sm font-bold text-[#111827] truncate ${mono ? "font-mono-num" : ""}`}>{value || "—"}</div>
        </div>
    );
}

function imageLabel(key) {
    const map = {
        img_front: "Frontansicht",
        img_rear: "Rückansicht",
        img_left: "Linke Seite",
        img_right: "Rechte Seite",
        img_papers: "Fahrzeugpapiere",
        img_odometer: "Tachostand",
        img_engine: "Motorraum",
        img_keys: "Schlüssel",
    };
    return map[key] || key;
}
